import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {StudyPhase, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import AddProcedureRunModal from 'studies/sidepanel/AddProcedureRunModal';
import AddDraftProcedureRunModal from 'studies/sidepanel/AddProcedureRunPlanModal';
import {
  getProcedureRunUIStringForStudyType,
  getProcedureTypeForStudyTypeAllowNull,
} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  AddProcedureRunButtonQuery,
  AddProcedureRunButtonQueryVariables,
} from './__generated/AddProcedureRunButtonQuery';

type StudyNode = ExtractByTypename<AddProcedureRunButtonQuery['study'], 'StudyNode'>;
interface ButtonProps {
  disabled: boolean;
  tooltipText: string | null;
}

export const ADD_PROCEDURE_RUN_BUTTON_QUERY = gql`
  query AddProcedureRunButtonQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        phase
        procedureRunCount
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function isButtonDisabled(study: StudyNode): ButtonProps {
  const buttonStatus: ButtonProps = {disabled: false, tooltipText: null};

  const studyType = study.studySchema.studyType;
  const procedureType = getProcedureTypeForStudyTypeAllowNull(studyType);
  if (procedureType == null || studyType === StudyType.CUSTOM) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `Procedure runs can not be added to this study`;
  } else if (!isFeatureEnabledForProcedureType(procedureType, ProceduresFeature.PLAN_EXPERIMENT)) {
    const procedureTypeName = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.SENTENCE,
      plural: true,
    });
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `You do not have sufficient permissions to use ${procedureTypeName}. Please contact your administrator.`;
  } else if (study.phase !== StudyPhase.EXECUTION) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `Reopen your study to add ${getProcedureRunUIStringForStudyType(studyType, {includeArticle: true})}${BenchlingConfig.STUDIES_ENABLE_PROCEDURE_RUN_DESIGN ? ' plan' : ''}`;
  } else if (study.procedureRunCount >= BenchlingConfig.STUDIES_MAX_NUM_RUNS_PER_PD_STUDY) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `Maximum number of ${getProcedureRunUIStringForStudyType(studyType, {plural: true})} reached`;
  }
  return buttonStatus;
}

/**
 * This component should only be rendered for study types that support multiple experiments.
 */
export default function AddProcedureRunButton({
  studyId,
  studyType,
}: {
  studyId: StrictString;
  studyType: StudyType;
}): JSX.Element {
  const result = useBenchlingQuery<AddProcedureRunButtonQuery, AddProcedureRunButtonQueryVariables>(
    ADD_PROCEDURE_RUN_BUTTON_QUERY,
    {variables: {studyId}, renderCachedDataWhenRefetching: true}
  );

  let buttonProps: ButtonProps = {disabled: true, tooltipText: null};
  let onClickHandler: () => void = noop;

  if (result.type === QueryResultType.PENDING) {
    buttonProps.tooltipText = 'Loading...';
  } else if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.study)) {
    const study: StudyNode = result.data.study;
    buttonProps = isButtonDisabled(study);

    onClickHandler = async () => {
      if (BenchlingConfig.STUDIES_ENABLE_PROCEDURE_RUN_DESIGN) {
        await legacyRunModal(AddDraftProcedureRunModal, {
          studyType: study.studySchema.studyType,
          studyId: study.id,
        });
      } else {
        await legacyRunModal(AddProcedureRunModal, {
          studyType: study.studySchema.studyType,
          studyId: study.id,
          hasExistingUnitOpsToLink: study.procedureRunCount > 0,
        });
      }
    };
  }

  return (
    <Tooltip isDisabled={!buttonProps.disabled} text={buttonProps.tooltipText}>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MEDIUM}
        fullWidth={false}
        icon="plus"
        text={`${BenchlingConfig.STUDIES_ENABLE_PROCEDURE_RUN_DESIGN ? 'Plan' : 'Add'} ${getProcedureRunUIStringForStudyType(studyType)}`}
        onClick={onClickHandler}
        disabled={buttonProps.disabled}
        dataTestAttrs={{component: 'AddProcedureRunModal', element: 'AddProcedureRunButton'}}
      />
    </Tooltip>
  );
}
